/**
 * bimTankRender — the water cistern's / treatment plant's shared drawing recipe.
 *
 * `utils/bimWaterTankParts.ts` owns the GEOMETRY (the module's own part boxes and
 * the water volume at a level); this module owns what those parts are painted
 * with and how a `box` / `cylinder` / `prism` part becomes geometry. The 3D
 * device-model layer (`composables/useBimDeviceModels.ts`) and the card preview
 * (`utils/deviceModelThumbnail.ts`) both read it, so a tank on a card and the
 * same tank standing in the house cannot drift apart.
 *
 * The palette is the module's own (`devices/water-tank/water_tank.py`,
 * `COLOR_*`): the SHELL takes the operator's RAL tone, drawn translucent so the
 * water line reads through the wall, and everything else is the product's
 * material.
 */

import * as THREE from 'three';

import { materialOf, unitBoxGeometry, unitCylinderGeometry } from './bimPrimitives';
import type { DeviceModel3d } from './bimDeviceModel';
import {
  waterTankParts,
  waterTankShape,
  type WaterTankPart,
  type WaterTankPartMaterial,
  type WaterTankShape,
} from './bimWaterTankParts';
import { wasteTankParts } from './bimWasteTankParts';

/** The cistern's non-shell tones (the module's `COLOR_*` constants). */
export const TANK_TONE = {
  water: [0.16, 0.4, 0.48] as const,
  surface: [0.46, 0.72, 0.78] as const,
  steel: [0.6, 0.615, 0.63] as const,
  pallet: [0.105, 0.11, 0.118] as const,
  trim: [0.075, 0.08, 0.085] as const,
  /** The treatment plant's murky grey-green. */
  wasteWater: [0.29, 0.37, 0.33] as const,
  wasteSurface: [0.4, 0.5, 0.45] as const,
  /** The green covers a biological plant wears (its own style in the module). */
  lid: [0.13, 0.3, 0.17] as const,
};

/** Which liquid a tank family holds — it decides the water's tone. */
export type TankKind = 'water' | 'waste';

/**
 * The shell's own opacity: LOW on purpose. The ask is that the water line reads
 * through the wall (owner: *"the walls of the cistern would be slightly
 * transparent … water level should be visible through the walls"*), and a milky
 * alpha hides the very thing the model exists to show — measured in the IFC
 * preview, where 0.45-opaque walls washed the water out completely.
 */
export const TANK_SHELL_OPACITY = 0.24;

export type TankMaterials = Record<WaterTankPartMaterial, THREE.MeshStandardMaterial>;

const tankMaterialCache = new Map<string, TankMaterials>();

export function tankMaterials(color: readonly number[], kind: TankKind = 'water'): TankMaterials {
  const key = `${kind}|${color.join(',')}`;
  const cached = tankMaterialCache.get(key);
  if (cached) return cached;
  const shell = materialOf(color, TANK_SHELL_OPACITY);
  shell.roughness = 0.2;
  shell.metalness = 0;
  // A translucent shell must not write depth: the water behind it would be
  // rejected by the depth test in whichever order the shell happened to draw.
  shell.depthWrite = false;
  const water = materialOf(kind === 'waste' ? TANK_TONE.wasteWater : TANK_TONE.water, 0.86);
  water.roughness = 0.12;
  water.metalness = 0.05;
  const surface = materialOf(kind === 'waste' ? TANK_TONE.wasteSurface : TANK_TONE.surface, 0.95);
  surface.roughness = 0.05;
  const steel = materialOf(TANK_TONE.steel);
  steel.metalness = 0.55;
  steel.roughness = 0.45;
  const pallet = materialOf(TANK_TONE.pallet);
  pallet.roughness = 0.8;
  const trim = materialOf(TANK_TONE.trim);
  trim.roughness = 0.65;
  const lid = materialOf(TANK_TONE.lid);
  lid.roughness = 0.6;
  const materials = { shell, water, surface, steel, pallet, trim, lid };
  tankMaterialCache.set(key, materials);
  return materials;
}

let unitCylinderX: THREE.CylinderGeometry | null = null;
let unitCylinderY: THREE.CylinderGeometry | null = null;

/**
 * The unit cylinder a tank part scales, with its AXIS along the named world
 * direction. `THREE.CylinderGeometry`'s own axis is +Y and its radius is 0.5 /
 * height 1, so a part's bbox IS its scale (`[dx, dy, dz]`).
 */
function tankCylinderGeometry(axis: 'x' | 'y' | 'z'): THREE.BufferGeometry {
  if (axis === 'z') return unitCylinderGeometry();
  if (axis === 'x') {
    if (!unitCylinderX) {
      unitCylinderX = new THREE.CylinderGeometry(0.5, 0.5, 1, 28);
      unitCylinderX.rotateZ(Math.PI / 2);
    }
    return unitCylinderX;
  }
  if (!unitCylinderY) unitCylinderY = new THREE.CylinderGeometry(0.5, 0.5, 1, 28);
  return unitCylinderY;
}

/**
 * A closed 2-D profile extruded along `axis` — the lying cistern's water is an
 * ellipse SEGMENT, which no scaled primitive can draw. `depth` is the part's
 * own length along the axis; the profile is centred on the part's centre.
 */
function tankPrismGeometry(
  profile: ReadonlyArray<readonly [number, number]>,
  axis: 'x' | 'z',
  depth: number,
): THREE.BufferGeometry {
  const n = profile.length;
  const half = depth / 2;
  const positions: number[] = [];
  const at = (point: readonly [number, number], t: number): [number, number, number] =>
    axis === 'z' ? [point[0], point[1], t] : [t, point[0], point[1]];
  for (const point of profile) positions.push(...at(point, -half));
  for (const point of profile) positions.push(...at(point, half));
  const indices: number[] = [];
  for (let i = 0; i < n; i += 1) {
    const j = (i + 1) % n;
    indices.push(i, j, j + n, i, j + n, i + n);
  }
  for (let i = 1; i < n - 1; i += 1) indices.push(0, i + 1, i);
  for (let i = 1; i < n - 1; i += 1) indices.push(n, n + i, n + i + 1);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

/** The geometry one tank part is drawn with, plus what the caller must dispose. */
export function tankPartGeometry(part: WaterTankPart): { geometry: THREE.BufferGeometry; owned: boolean } {
  if (part.shape === 'cylinder') {
    return { geometry: tankCylinderGeometry(part.axis ?? 'z'), owned: false };
  }
  if (part.shape === 'prism' && part.profile) {
    // The extrusion runs along the part's own axis, so its LENGTH is that
    // axis's extent (X for the lying cistern's water, Z for a standing prism).
    const axis: 'x' | 'z' = part.axis === 'x' ? 'x' : 'z';
    const depth = axis === 'x' ? part.size[0] : part.size[2];
    return { geometry: tankPrismGeometry(part.profile, axis, depth), owned: true };
  }
  return { geometry: unitBoxGeometry(), owned: false };
}

/** The cistern shape a slot names (a stored typo falls back to the IBC). */
export function tankShapeOf(library: DeviceModel3d['library']): WaterTankShape {
  return waterTankShape(library?.prefs.shape) ?? 'ibc';
}

/** Which tank family this slot is: the cistern, or the treatment plant. */
export function tankKindOf(library: DeviceModel3d['library']): TankKind {
  return library?.type === 'waste-tank' ? 'waste' : 'water';
}

/** Both tank families' parts at a level — one recipe draws either. */
export function tankPartsFor(library: DeviceModel3d['library'], levelPct: unknown): WaterTankPart[] {
  return tankKindOf(library) === 'waste'
    ? wasteTankParts(levelPct)
    : waterTankParts(tankShapeOf(library), Number(levelPct));
}

/** A tank's solid parts cast; its glassy ones (shell, water, surface) do not. */
export function tankCastsShadow(material: WaterTankPartMaterial): boolean {
  return material !== 'shell' && material !== 'water' && material !== 'surface';
}
